/**
 * Centralny monitoring blędów frontend → backend (/api/monitoring/client-error).
 * Łapie:
 *  - window.onerror (JS errors)
 *  - window.onunhandledrejection (Promise rejections)
 *  - console.error (opcjonalnie zostawione - tylko proxy bez side-effectow)
 *
 * Init z src/index.js: initErrorMonitor()
 * Manualne raportowanie: reportError({ message, stack?, level?, extra? })
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ENDPOINT = `${API_URL}/api/monitoring/client-error`;
const REPORT_ENDPOINT = `${API_URL}/api/monitoring/user-report`;

// Dedup: ten sam blad w ciagu N ms wysylamy raz
const RECENT = new Map();
const DEDUP_WINDOW_MS = 5000;
const MAX_REPORTS_PER_MIN = 30;
let reportsThisMin = 0;
let reportsMinuteResetAt = Date.now() + 60_000;

function rateLimitOk() {
    const now = Date.now();
    if (now > reportsMinuteResetAt) {
        reportsThisMin = 0;
        reportsMinuteResetAt = now + 60_000;
    }
    if (reportsThisMin >= MAX_REPORTS_PER_MIN) return false;
    reportsThisMin += 1;
    return true;
}

function dedupKey({ message, stack, route }) {
    const m = (message || '').slice(0, 200);
    const s = (stack || '').slice(0, 200);
    return `${route || ''}|${m}|${s}`;
}

function isRecent(key) {
    const now = Date.now();
    const last = RECENT.get(key);
    if (last && now - last < DEDUP_WINDOW_MS) return true;
    RECENT.set(key, now);
    // GC starych wpisow
    if (RECENT.size > 100) {
        for (const [k, t] of RECENT) {
            if (now - t > DEDUP_WINDOW_MS) RECENT.delete(k);
        }
    }
    return false;
}

function getAuthHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    try {
        const token = localStorage.getItem('token');
        if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (_) { /* localStorage moze byc niedostepny */ }
    return headers;
}

async function postSafe(url, payload) {
    try {
        // keepalive zeby zalogowac nawet jak strona jest zamykana
        await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(payload),
            keepalive: true,
        });
    } catch (_) {
        // celowo ignorujemy - nie wpadamy w petle "blad zglaszania bledow"
    }
}

/**
 * Reportuje blad do backendu (kolekcja error_logs).
 * @param {Object} p
 * @param {string} p.message
 * @param {string} [p.stack]
 * @param {string} [p.level]  'error' | 'warning' | 'info'
 * @param {string} [p.route]
 * @param {string} [p.url]
 * @param {Object} [p.extra]
 */
export function reportError(p) {
    if (!p || !p.message) return;
    if (!rateLimitOk()) return;

    const payload = {
        message: String(p.message).slice(0, 1000),
        stack: p.stack ? String(p.stack).slice(0, 5000) : null,
        url: p.url || (typeof window !== 'undefined' ? window.location.href : null),
        route: p.route || (typeof window !== 'undefined' ? window.location.pathname : null),
        level: p.level || 'error',
        extra: p.extra || null,
    };

    if (isRecent(dedupKey(payload))) return;
    postSafe(ENDPOINT, payload);
}

/**
 * Wysyla zgloszenie od uzytkownika ("Zglos blad").
 * @param {Object} p
 * @param {string} p.message
 * @param {string} [p.contact_email]
 * @param {Object} [p.extra]
 */
export async function sendUserReport(p) {
    if (!p || !p.message) throw new Error('Wiadomosc jest wymagana');
    const payload = {
        message: String(p.message).slice(0, 2000),
        contact_email: p.contact_email || null,
        url: typeof window !== 'undefined' ? window.location.href : null,
        extra: p.extra || null,
    };
    const res = await fetch(REPORT_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Nie udalo sie wyslac zgloszenia');
    return res.json();
}

let installed = false;

export function initErrorMonitor() {
    if (installed || typeof window === 'undefined') return;
    installed = true;

    // 1) Synchroniczne wyjatki JS
    window.addEventListener('error', (event) => {
        // Filtr: bledy ladowania zasobow (img/script) maja event.target ale brak event.error
        if (event && event.target && (event.target.tagName === 'IMG' || event.target.tagName === 'SCRIPT' || event.target.tagName === 'LINK')) {
            reportError({
                level: 'warning',
                message: `Resource load failed: ${event.target.tagName} ${event.target.src || event.target.href}`,
                extra: { type: 'resource' },
            });
            return;
        }
        reportError({
            message: event.message || 'Unknown error',
            stack: event.error && event.error.stack ? event.error.stack : null,
            extra: {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
            },
        });
    }, true);

    // 2) Odrzucone promise
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        let message = 'Unhandled promise rejection';
        let stack = null;
        if (reason instanceof Error) {
            message = reason.message || message;
            stack = reason.stack || null;
        } else if (typeof reason === 'string') {
            message = reason;
        } else if (reason) {
            try { message = JSON.stringify(reason).slice(0, 500); } catch (_) {}
        }
        reportError({ message, stack, extra: { type: 'unhandledrejection' } });
    });
}

// Wstawiamy reportError do window zeby dalo sie wywolac z dowolnego komponentu / DevTools
if (typeof window !== 'undefined') {
    window.__klasoReport = reportError;
    window.__klasoUserReport = sendUserReport;
}
